import { Fragment, useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import MeasuredChartFrame from "./MeasuredChartFrame";

import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";

const TICK = { fill: "#d6d5ea", fontSize: 12 };

const COLORS = [
    "#6F3AFF",
    "#52EA91",
    "#FFB347",
    "#39C0ED",
    "#FF6F91",
    "#8DD3C7",
    "#C9A227",
    "#A78BFA",
    "#3AA6FF",
    "#F59E0B",
];

function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function safeKey(value) {
    return String(value || "UNKNOWN")
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();
}

function formatDate(dateStr) {
    if (!dateStr) return "";

    const d = new Date(`${dateStr}T00:00:00`);

    if (Number.isNaN(d.getTime())) {
        return String(dateStr);
    }

    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

function formatInt(value) {
    return Number(value ?? 0).toLocaleString("de-DE");
}

function getOpenClosed(value) {
    return {
        open: toNumber(
            value?.OPEN ??
            value?.open ??
            value?.open_cases ??
            value?.openCases ??
            value?.current?.OPEN,
            0
        ),
        closed: toNumber(
            value?.CLOSED ??
            value?.closed ??
            value?.closed_cases ??
            value?.closedCases ??
            value?.current?.CLOSED,
            0
        ),
    };
}

function getRowsArray(raw) {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.rows)) return raw.rows;
    if (Array.isArray(raw?.items)) return raw.items;
    return [];
}

function normalizeFlatRows(rows) {
    const byDate = new Map();
    const typeNames = new Set();

    rows.forEach((row) => {
        const date = row.date ?? row.day ?? row.period ?? row.label;
        const typeName =
            row.remediation_type ??
            row.REMEDIATION_TYPE ??
            row.type_name ??
            row.TYPE_NAME ??
            row.name;

        if (!date || !typeName) return;

        const rowState = byDate.get(date) || {
            date,
            TOTAL_OPEN: 0,
            TOTAL_CLOSED: 0,
        };

        const { open, closed } = getOpenClosed(row);
        const key = safeKey(typeName);

        rowState[`OPEN__${key}`] = open;
        rowState[`CLOSED__${key}`] = closed;
        rowState.TOTAL_OPEN += open;
        rowState.TOTAL_CLOSED += closed;

        typeNames.add(String(typeName));
        byDate.set(date, rowState);
    });

    return {
        rows: Array.from(byDate.values()),
        typeNames: Array.from(typeNames),
    };
}

function normalizeNestedRows(rows) {
    const typeNames = new Set();

    const normalizedRows = rows.map((row) => {
        const date = row.date ?? row.day ?? row.period ?? row.label;
        const byType =
            row.remediation_types ??
            row.by_remediation_type ??
            row.by_type ??
            row.types ??
            {};

        const nextRow = {
            date,
            TOTAL_OPEN: 0,
            TOTAL_CLOSED: 0,
        };

        Object.entries(byType).forEach(([typeName, value]) => {
            const { open, closed } = getOpenClosed(value);
            const key = safeKey(typeName);

            nextRow[`OPEN__${key}`] = open;
            nextRow[`CLOSED__${key}`] = closed;
            nextRow.TOTAL_OPEN += open;
            nextRow.TOTAL_CLOSED += closed;

            typeNames.add(String(typeName));
        });

        const total = row.total ?? row.TOTAL ?? row.all ?? null;

        if (total) {
            const { open, closed } = getOpenClosed(total);
            nextRow.TOTAL_OPEN = open;
            nextRow.TOTAL_CLOSED = closed;
        }

        return nextRow;
    });

    return {
        rows: normalizedRows,
        typeNames: Array.from(typeNames),
    };
}

function normalizePayload(raw) {
    const rows = getRowsArray(raw);

    const hasFlatShape = rows.some(
        (row) =>
            row?.remediation_type ||
            row?.REMEDIATION_TYPE ||
            row?.type_name ||
            row?.TYPE_NAME
    );

    return hasFlatShape ? normalizeFlatRows(rows) : normalizeNestedRows(rows);
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;

    return (
        <div className="remediation-dashboard-tooltip">
            <div className="remediation-dashboard-tooltip-title">
                {formatDate(label)}
            </div>

            {payload.map((point) => (
                <div
                    key={point.dataKey}
                    style={{
                        color: point.stroke,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 20,
                    }}
                >
                    <span>{point.name}</span>
                    <strong>{formatInt(point.value)}</strong>
                </div>
            ))}
        </div>
    );
}

export default function CaseTrendByRemediationType() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [rows, setRows] = useState([]);
    const [typeNames, setTypeNames] = useState([]);
    const [selectedType, setSelectedType] = useState("__all__");

    useEffect(() => {
        let cancelled = false;

        const fetchStats = async () => {
            try {
                setLoading(true);
                setError("");

                const response = await fetchWithAuth(
                    `${API_BASE}/ainstein/in/dashboard?chart_key=case_trend_by_remediation_type`
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const payload = await response.json();
                const normalized = normalizePayload(payload);

                if (!cancelled) {
                    setRows(normalized.rows);
                    setTypeNames(normalized.typeNames);
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load remediation type trend");
                    setRows([]);
                    setTypeNames([]);
                    setLoading(false);
                }
            }
        };

        fetchStats();

        return () => {
            cancelled = true;
        };
    }, []);

    const visibleTypes = useMemo(() => {
        if (selectedType === "__all__") return typeNames;
        return typeNames.filter((typeName) => typeName === selectedType);
    }, [selectedType, typeNames]);

    return (
        <section className="remediation-dashboard-section">
            <article className="remediation-dashboard-card">
                <div className="remediation-dashboard-card-headline">
                    <div>
                        <h3 className="remediation-dashboard-card-title">
                            Open / Closed Cases by Remediation Type
                        </h3>
                        <p className="remediation-dashboard-card-subtitle">
                            Total overview plus drill-down by remediation type
                        </p>
                    </div>

                    {typeNames.length > 0 && (
                        <label className="remediation-dashboard-control">
                            <span>Category</span>
                            <select
                                value={selectedType}
                                onChange={(event) => setSelectedType(event.target.value)}
                            >
                                <option value="__all__">All remediation types</option>
                                {typeNames.map((typeName) => (
                                    <option key={typeName} value={typeName}>
                                        {typeName}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>

                {loading && (
                    <div className="remediation-dashboard-loading">
                        <span className="remediation-dashboard-loading-spinner" />
                        <span>Loading data...</span>
                    </div>
                )}

                {!loading && (error || rows.length === 0) && (
                    <div className="remediation-dashboard-empty-state">
                        {error || "No remediation type trend data available"}
                    </div>
                )}

                {!loading && !error && rows.length > 0 && (
                    <MeasuredChartFrame height={420}>
                        {({ width, height }) => (
                            <LineChart
                                width={width}
                                height={height}
                                data={rows}
                                margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="rgba(255,255,255,0.06)"
                                />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatDate}
                                    tick={TICK}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={TICK}
                                    axisLine={false}
                                    tickLine={false}
                                    width={56}
                                    tickFormatter={formatInt}
                                />
                                <Tooltip
                                    cursor={{ stroke: "rgba(80,178,255,0.25)", strokeWidth: 1 }}
                                    content={<CustomTooltip />}
                                />
                                <Legend
                                    wrapperStyle={{
                                        fontSize: 12,
                                        color: "#d6d5ea",
                                        paddingTop: 8,
                                    }}
                                    formatter={(value) => (
                                        <span style={{ color: "#e5e8f3" }}>{value}</span>
                                    )}
                                />

                                <Line
                                    type="monotone"
                                    dataKey="TOTAL_OPEN"
                                    name="Total Open"
                                    stroke="#EDEBFF"
                                    strokeWidth={2.8}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                />

                                <Line
                                    type="monotone"
                                    dataKey="TOTAL_CLOSED"
                                    name="Total Closed"
                                    stroke="#EDEBFF"
                                    strokeWidth={2.8}
                                    strokeDasharray="6 5"
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                />

                                {visibleTypes.map((typeName, index) => {
                                    const color = COLORS[index % COLORS.length];
                                    const key = safeKey(typeName);

                                    return (
                                        <Fragment key={typeName}>
                                            <Line
                                                type="monotone"
                                                dataKey={`OPEN__${key}`}
                                                name={`${typeName} Open`}
                                                stroke={color}
                                                strokeWidth={2}
                                                dot={false}
                                                activeDot={{ r: 4 }}
                                                connectNulls
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey={`CLOSED__${key}`}
                                                name={`${typeName} Closed`}
                                                stroke={color}
                                                strokeWidth={2}
                                                strokeDasharray="6 5"
                                                dot={false}
                                                activeDot={{ r: 4 }}
                                                connectNulls
                                            />
                                        </Fragment>
                                    );
                                })}
                            </LineChart>
                        )}
                    </MeasuredChartFrame>
                )}
            </article>
        </section>
    );
}